import { AwsRum, AwsRumConfig } from 'aws-rum-web';
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import { store } from "../app/store"
import { version } from '../../../package.json'
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getWebuid } from '../config/okta.config';

enum ELogTypeAws {
    Error = "Error",
    Warning = "Warning",
    Notice = "Notice",
    Info = "Info",
    Debug = "Debug",
    None = "None",
}

const { VITE_OKTA_ISSUER, VITE_AWS_REGION, VITE_AWS_RUM_APP_ID, VITE_AWS_COGNITO_IDENTITY_POOL_ID } = import.meta.env

const OIDC_PROVIDER = VITE_OKTA_ISSUER.replace(/^https:\/\//, ""); // match what’s registered in IAM, without https://

let awsRumClient: AwsRum | undefined = undefined;
async function getAwsCredentials(jwt_token: string) {
    try {
        if (!jwt_token || jwt_token.trim() === '') {
            console.error("JWT token is required to get AWS credentials");
            return;
        }
        // Create a credential provider from Cognito Identity Pool with your JWT
        const credentialProvider = fromCognitoIdentityPool({
            clientConfig: { region: VITE_AWS_REGION },
            identityPoolId: VITE_AWS_COGNITO_IDENTITY_POOL_ID,
            logins: {
                [OIDC_PROVIDER]: jwt_token,
            },
        });
        return await credentialProvider();
    }
    catch (error) {
        console.error("Error getting AWS credentials:", error);
        throw error; // Re-throw the error to be handled by the caller
    }
}

function buildAWSRUM(awsCredentials) {
    try {
        const config: AwsRumConfig = {
            sessionSampleRate: 1,
            endpoint: "https://dataplane.rum.us-west-2.amazonaws.com",
            telemetries: [[
                'performance',
                {
                    ignore: (entry: PerformanceEntry) => {
                        return entry.entryType === 'resource';
                    }
                }
            ], "errors", "http"],
            allowCookies: true,
            enableXRay: false,
            sessionAttributes: {
                user: store?.getState()?.userService?.user?.email,
                userId: store?.getState()?.userService?.user?.user_id,
                caseId:'undefined',
                webUUID: getWebuid()
            },
            signing: true,
            disableAutoPageView: true,
            sessionLengthSeconds: 10
        };

        const APPLICATION_VERSION: string = version;

        const awsRum: AwsRum = new AwsRum(
            VITE_AWS_RUM_APP_ID,
            APPLICATION_VERSION,
            VITE_AWS_REGION,
            config
        );
        awsRum.setAwsCredentials(awsCredentials);
        awsRum.enable();
        console.log("AWS RUM web client initialized with Cognito Identity Pool credentials");
        return awsRum;
    } catch (error) {
        console.error("Error initializing CloudWatch RUM web client:", error);
        // Ignore errors thrown during CloudWatch RUM web client initialization
    }
}

function loadAWSRUMClient(jwt: string): AwsRum | undefined {
    if (VITE_AWS_RUM_APP_ID === undefined || VITE_AWS_RUM_APP_ID.trim() === '') {
        console.info("AWS RUM App ID is not set. Skipping AWS RUM client initialization.");
        return;
    }
    if (!jwt || jwt.trim() === '') {
        console.info("JWT token is required to load AWS RUM");
        return;
    }
    const awsCredentials = getAwsCredentials(jwt).catch(console.error);
    awsRumClient = buildAWSRUM(awsCredentials);
}

function getAWSRUMClient(): AwsRum | undefined {
    if (!awsRumClient) {
        console.info("AWS RUM client is not initialized. Call loadAWSRUM first.");
        return;
    }
    return awsRumClient;
}


function useAwsRumRouteTracking() {
  const location = useLocation();

  useEffect(() => {
      getAWSRUMClient()?.recordPageView({
        pageId: location.pathname.replace(/([0-9]+)/g, "*"),
        pageAttributes: {
            actualPath: location.pathname
        }
      });
  }, [location]);
}

function logAws(level: ELogTypeAws, messageData: {}) {
    const awsRumClient = getAWSRUMClient()
    if (!awsRumClient) {
        return;
    }
    const eventType = "ucloud_event";
    // log data
    const logData = {
        level: level,
        eventType: eventType,
        timestamp: new Date().toString(),
        ...messageData,
    };
    // record
    awsRumClient.recordEvent(eventType, logData);
    // switch (level) {
    //     case ELogTypeAws.Error:
    //         // record Error, use recordError
    //         awsRumClient.recordError({
    //             customData: { ...logData },
    //         });
    //         break;
    //     default:
    //         // other level：use recordEvent
    //         awsRumClient.recordEvent(`${level}_${eventType}`, {
    //             customData: { ...logData },
    //         });
    //         break; 
    // }
}

export { loadAWSRUMClient, getAWSRUMClient, useAwsRumRouteTracking, logAws, ELogTypeAws};